import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, AlertTriangle, ChevronLeft, ChevronRight, Flag, User, X, ExternalLink } from 'lucide-react';
import { schedulingService } from '../../services/schedulingService';

const CaseTimeline = ({ caseId, includeCompleted = false, onTaskClick }) => {
  const navigate = useNavigate();
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewRange, setViewRange] = useState({ start: null, end: null });
  const [zoom, setZoom] = useState('month'); // 'week', 'month', 'quarter'
  const [selectedTask, setSelectedTask] = useState(null);

  const handleViewTask = (taskId) => {
    navigate(`/tasks/${taskId}`);
  };

  const fetchTimeline = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await schedulingService.getCaseTimeline(caseId, includeCompleted);
      setTimeline(response.data);

      // Set initial view range based on data
      if (response.data?.tasks?.length > 0 || response.data?.deadlines?.length > 0) {
        const dates = [
          ...response.data.tasks.map(t => t.scheduled_start || t.early_start || t.due_date).filter(Boolean),
          ...response.data.deadlines.map(d => d.deadline_date).filter(Boolean)
        ].map(d => new Date(d));

        if (dates.length > 0) {
          const minDate = new Date(Math.min(...dates));
          const maxDate = new Date(Math.max(...dates));

          // Add padding
          minDate.setDate(minDate.getDate() - 7);
          maxDate.setDate(maxDate.getDate() + 7);

          setViewRange({ start: minDate, end: maxDate });
        }
      }
    } catch (err) {
      console.error('Error fetching timeline:', err);
      setError('Failed to load timeline');
    } finally {
      setLoading(false);
    }
  }, [caseId, includeCompleted]);

  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline]);

  // Calculate timeline dimensions
  const { dayWidth, totalDays, dayColumns } = useMemo(() => {
    if (!viewRange.start || !viewRange.end) {
      return { dayWidth: 40, totalDays: 30, dayColumns: [] };
    }

    const days = Math.ceil((viewRange.end - viewRange.start) / (1000 * 60 * 60 * 24));
    const width = zoom === 'week' ? 80 : zoom === 'month' ? 40 : 20;

    const columns = [];
    const current = new Date(viewRange.start);
    while (current <= viewRange.end) {
      columns.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return { dayWidth: width, totalDays: days, dayColumns: columns };
  }, [viewRange, zoom]);

  const getTaskPosition = (task) => {
    if (!viewRange.start) return null;

    const startDate = new Date(task.scheduled_start || task.early_start || task.due_date);
    const endDate = task.scheduled_end
      ? new Date(task.scheduled_end)
      : new Date(startDate.getTime() + (task.estimated_hours || 1) * 60 * 60 * 1000);

    const startOffset = Math.max(0, Math.ceil((startDate - viewRange.start) / (1000 * 60 * 60 * 24)));
    const duration = Math.max(1, Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)));

    return {
      left: startOffset * dayWidth,
      width: Math.max(duration * dayWidth - 4, dayWidth - 4)
    };
  };

  const getDeadlinePosition = (deadline) => {
    if (!viewRange.start) return null;

    const deadlineDate = new Date(deadline.deadline_date);
    const offset = Math.ceil((deadlineDate - viewRange.start) / (1000 * 60 * 60 * 24));

    return { left: offset * dayWidth };
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getTaskColor = (task) => {
    if (task.is_critical_path) return 'bg-red-500 border-red-600';
    if (task.status === 'completed') return 'bg-green-400 border-green-500';
    if (task.status === 'in_progress') return 'bg-blue-500 border-blue-600';
    return 'bg-gray-400 border-gray-500';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'completed': 'Completed',
      'in_progress': 'In Progress',
      'todo': 'To Do',
    };
    return labels[status] || status;
  };

  const navigateTimeline = (direction) => {
    const days = zoom === 'week' ? 7 : zoom === 'month' ? 30 : 90;
    const offset = direction * days * 24 * 60 * 60 * 1000;

    setViewRange(prev => ({
      start: new Date(prev.start.getTime() + offset),
      end: new Date(prev.end.getTime() + offset)
    }));
  };

  const handleTaskBarClick = (task) => {
    setSelectedTask(selectedTask?.id === task.id ? null : task);
    if (onTaskClick) {
      onTaskClick(task);
    }
  };

  // Build tooltip text for task bars
  const getTaskTooltip = (task) => {
    const lines = [task.title];
    if (task.assigned_to_name) lines.push(`Assigned: ${task.assigned_to_name}`);
    if (task.status) lines.push(`Status: ${getStatusLabel(task.status)}`);
    if (task.due_date) lines.push(`Due: ${new Date(task.due_date).toLocaleDateString()}`);
    if (task.is_critical_path) lines.push('⚠ Critical Path');
    return lines.join('\n');
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-48"></div>
          <div className="h-64 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border p-6 text-center">
        <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
        <p className="text-gray-600">{error}</p>
        <button onClick={fetchTimeline} className="mt-2 text-blue-600 hover:text-blue-800">
          Retry
        </button>
      </div>
    );
  }

  if (!timeline) {
    return null;
  }

  // Empty state
  if (timeline.tasks.length === 0 && timeline.deadlines.length === 0) {
    return (
      <div className="bg-white rounded-lg border p-8 text-center">
        <Calendar className="h-12 w-12 mx-auto text-gray-300 mb-3" />
        <h3 className="text-lg font-medium text-gray-900 mb-1">No Scheduled Tasks</h3>
        <p className="text-gray-500 text-sm">
          Add tasks to projects and set deadlines to see your case timeline.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border">
      {/* Header */}
      <div className="p-4 border-b flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center">
          <Calendar className="h-5 w-5 mr-2" />
          Case Timeline
        </h3>

        <div className="flex items-center space-x-4">
          {/* Zoom Controls */}
          <div className="flex items-center space-x-1 border rounded">
            {['week', 'month', 'quarter'].map(z => (
              <button
                key={z}
                onClick={() => setZoom(z)}
                className={`px-3 py-1 text-sm capitalize ${
                  zoom === z ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {z}
              </button>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center space-x-1">
            <button
              onClick={() => navigateTimeline(-1)}
              className="p-1 hover:bg-gray-100 rounded"
              title="Previous"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={() => navigateTimeline(1)}
              className="p-1 hover:bg-gray-100 rounded"
              title="Next"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="px-4 py-2 bg-gray-50 border-b flex items-center space-x-6 text-sm">
        <div>
          <span className="text-gray-500">Tasks:</span>{' '}
          <span className="font-medium">{timeline.summary.total_tasks}</span>
        </div>
        <div>
          <span className="text-gray-500">Critical:</span>{' '}
          <span className="font-medium text-red-600">{timeline.summary.critical_path_tasks}</span>
        </div>
        <div>
          <span className="text-gray-500">Deadlines:</span>{' '}
          <span className="font-medium">{timeline.summary.total_deadlines}</span>
        </div>
        <div>
          <span className="text-gray-500">Float:</span>{' '}
          <span className="font-medium">{timeline.summary.total_float_days?.toFixed(1) || 0} days</span>
        </div>
      </div>

      {/* Timeline View */}
      <div className="overflow-x-auto">
        <div style={{ minWidth: totalDays * dayWidth + 320 }}>
          {/* Date Headers */}
          <div className="flex border-b">
            <div className="w-72 flex-shrink-0 p-2 font-medium text-gray-700 bg-gray-50">
              Task
            </div>
            <div className="flex-1 flex">
              {dayColumns.map((date, idx) => (
                <div
                  key={idx}
                  style={{ width: dayWidth }}
                  className={`text-xs text-center py-1 border-l ${
                    date.getDay() === 0 || date.getDay() === 6 ? 'bg-gray-100' : ''
                  } ${date.getDate() === 1 ? 'font-medium' : ''}`}
                >
                  {date.getDate() === 1 || idx === 0 ? formatDate(date) : date.getDate()}
                </div>
              ))}
            </div>
          </div>

          {/* Deadlines Row */}
          {timeline.deadlines.length > 0 && (
            <div className="flex border-b bg-red-50">
              <div className="w-72 flex-shrink-0 p-2 text-sm font-medium text-red-700">
                Deadlines
              </div>
              <div className="flex-1 relative h-10">
                {timeline.deadlines.map((deadline, idx) => {
                  const pos = getDeadlinePosition(deadline);
                  if (!pos) return null;

                  return (
                    <div
                      key={idx}
                      className="absolute top-1 flex flex-col items-center cursor-pointer group"
                      style={{ left: pos.left }}
                      title={`${deadline.title}\n${new Date(deadline.deadline_date).toLocaleDateString()}`}
                    >
                      <Flag className="h-5 w-5 text-red-600 group-hover:text-red-800" />
                      <div className="text-xs text-red-700 whitespace-nowrap max-w-24 truncate group-hover:max-w-none group-hover:bg-white group-hover:px-1 group-hover:rounded group-hover:shadow">
                        {deadline.title}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Task Rows */}
          {timeline.tasks.map((task) => {
            const pos = getTaskPosition(task);
            const isSelected = selectedTask?.id === task.id;

            return (
              <div
                key={task.id}
                className={`flex border-b transition-colors ${
                  task.is_critical_path ? 'bg-red-50' : ''
                } ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
              >
                {/* Task name column - clickable to navigate */}
                <div className="w-72 flex-shrink-0 p-3 border-r">
                  <div
                    className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer line-clamp-2"
                    onClick={() => handleViewTask(task.id)}
                    title="Click to view task details"
                  >
                    {task.title}
                  </div>
                  <div className="text-xs text-gray-500 flex items-center gap-2 mt-1">
                    {task.assigned_to_name && (
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        <span className="truncate max-w-24">{task.assigned_to_name}</span>
                      </span>
                    )}
                    <span className={`px-1.5 py-0.5 rounded text-xs ${
                      task.status === 'completed' ? 'bg-green-100 text-green-700' :
                      task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {getStatusLabel(task.status)}
                    </span>
                    {task.is_critical_path && (
                      <span className="text-red-600 font-medium">Critical</span>
                    )}
                  </div>
                </div>

                {/* Task bar area */}
                <div className="flex-1 relative h-16">
                  {/* Grid Lines */}
                  <div className="absolute inset-0 flex">
                    {dayColumns.map((date, i) => (
                      <div
                        key={i}
                        style={{ width: dayWidth }}
                        className={`border-l h-full ${
                          date.getDay() === 0 || date.getDay() === 6 ? 'bg-gray-50' : ''
                        }`}
                      />
                    ))}
                  </div>

                  {/* Task Bar - click to show preview panel */}
                  {pos && (
                    <div
                      className={`absolute top-3 h-10 rounded-md border-2 ${getTaskColor(task)} cursor-pointer
                        hover:shadow-lg hover:brightness-110 transition-all ${
                          isSelected ? 'ring-2 ring-blue-400 ring-offset-1' : ''
                        }`}
                      style={{ left: pos.left, width: pos.width }}
                      onClick={() => handleTaskBarClick(task)}
                      title={getTaskTooltip(task)}
                    />
                  )}

                  {/* Float indicator */}
                  {pos && task.total_float > 0 && (
                    <div
                      className="absolute top-3 h-10 border-2 border-dashed border-gray-300 rounded-md opacity-50"
                      style={{
                        left: pos.left + pos.width,
                        width: Math.min(task.total_float * dayWidth, 200)
                      }}
                      title={`Float: ${task.total_float.toFixed(1)} days`}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected Task Detail Panel */}
      {selectedTask && (
        <div className="border-t bg-blue-50 p-4">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h4 className="font-semibold text-gray-900">{selectedTask.title}</h4>
                <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${
                  selectedTask.status === 'completed' ? 'bg-green-100 text-green-800' :
                  selectedTask.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {selectedTask.status?.replace('_', ' ')}
                </span>
                {selectedTask.is_critical_path && (
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                    Critical Path
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                {selectedTask.assigned_to_name && (
                  <div>
                    <span className="text-gray-500">Assigned to:</span>{' '}
                    <span className="font-medium">{selectedTask.assigned_to_name}</span>
                  </div>
                )}
                {selectedTask.due_date && (
                  <div>
                    <span className="text-gray-500">Due:</span>{' '}
                    <span className="font-medium">{new Date(selectedTask.due_date).toLocaleDateString()}</span>
                  </div>
                )}
                {selectedTask.estimated_hours && (
                  <div>
                    <span className="text-gray-500">Est. hours:</span>{' '}
                    <span className="font-medium">{selectedTask.estimated_hours}h</span>
                  </div>
                )}
                <div>
                  <span className="text-gray-500">Float:</span>{' '}
                  <span className={`font-medium ${selectedTask.total_float <= 0 ? 'text-red-600' : ''}`}>
                    {selectedTask.total_float?.toFixed(1) || 0} days
                  </span>
                </div>
              </div>
              {selectedTask.description && (
                <p className="text-sm text-gray-600 mt-2">{selectedTask.description}</p>
              )}
              <button
                onClick={() => handleViewTask(selectedTask.id)}
                className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
                View Full Task
              </button>
            </div>
            <button
              onClick={() => setSelectedTask(null)}
              className="text-gray-400 hover:text-gray-600 ml-4 p-1"
              title="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="px-4 py-3 border-t bg-gray-50 flex flex-wrap items-center gap-4 text-sm">
        <div className="flex items-center">
          <div className="w-4 h-4 rounded bg-red-500 mr-1.5"></div>
          <span>Critical Path</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 rounded bg-blue-500 mr-1.5"></div>
          <span>In Progress</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 rounded bg-green-400 mr-1.5"></div>
          <span>Completed</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 rounded bg-gray-400 mr-1.5"></div>
          <span>To Do</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 rounded border-2 border-dashed border-gray-300 mr-1.5"></div>
          <span>Float</span>
        </div>
        <div className="flex items-center">
          <Flag className="h-4 w-4 text-red-600 mr-1.5" />
          <span>Deadline</span>
        </div>
        <div className="ml-auto text-xs text-gray-500">
          Click task name to open • Click bar for preview
        </div>
      </div>
    </div>
  );
};

export default CaseTimeline;
